import React from 'react'

import { PDFLogo } from '@components/PDFLogo'
import {
	Page,
	Text,
	View,
	Document,
	StyleSheet,
	Svg,
	Path,
} from '@react-pdf/renderer'
import { PDFData } from '@utils/reportAdapter'

const COLORS = {
	blue: '#3b82f6',
	blueLight: '#eff6ff',
	blueBorder: '#bfdbfe',
	blueText: '#2563eb',
	borderLeft: '#60a5fa',
	slateDark: '#334155',
	slateLight: '#e2e8f0',
	bgGray: '#f9fafb',
	white: '#ffffff',
	red: '#DC2626',
	softRed: '#e05151',
	orange: '#f97316',
	green: '#16a34a',
	brickRed: '#c0392b',
	darkOrange: '#e67e22',
	darkGreen: '#2e8b57',
	purple: '#8e44ad',
	yellowDark: '#f39c12',
	yellowGradient: '#ffeebb',
	yellowText: '#fde047',
	brownText: '#713f12',
}

const styles = StyleSheet.create({
	headerLogo: {
		width: 120,
		height: 'auto',
		alignSelf: 'center',
		marginBottom: 20,
	},
	page: {
		padding: 30,
		fontFamily: 'Helvetica',
		backgroundColor: COLORS.white,
	},
	mainWrapper: {
		flexDirection: 'column',
		gap: 20,
	},
	sectionBox: {
		backgroundColor: COLORS.bgGray,
		padding: 20,
		borderRadius: 6,
		borderLeftWidth: 6,
		borderLeftColor: COLORS.borderLeft,
		marginBottom: 20,
	},
	sectionHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		marginBottom: 10,
	},
	sectionTitle: {
		fontSize: 16,
		color: COLORS.blueText,
		fontWeight: 'bold',
		marginLeft: 6,
	},
	sectionTitleNoIcon: {
		fontSize: 16,
		color: COLORS.blueText,
		fontWeight: 'bold',
		marginLeft: 0,
	},
	divider: {
		borderBottomWidth: 1,
		borderBottomColor: COLORS.blueBorder,
		marginBottom: 12,
	},
	textSm: {
		fontSize: 9,
		color: COLORS.slateDark,
		lineHeight: 1.4,
	},
	bold: {
		fontWeight: 'bold',
		fontFamily: 'Helvetica-Bold',
	},
	subTitle: {
		fontSize: 14,
		color: COLORS.slateDark,
		fontWeight: 'bold',
		marginTop: 15,
		marginBottom: 2,
	},
	colorBar: {
		height: 4,
		borderRadius: 2,
		marginBottom: 10,
		opacity: 0.8,
	},
	statsBox: {
		backgroundColor: COLORS.blueLight,
		borderWidth: 1,
		borderColor: COLORS.blueBorder,
		borderRadius: 8,
		padding: 12,
		marginTop: 10,
		position: 'relative',
	},
	badge: {
		backgroundColor: COLORS.blue,
		color: COLORS.white,
		fontSize: 8,
		paddingVertical: 2,
		paddingHorizontal: 6,
		borderRadius: 4,
		position: 'absolute',
		top: -8,
		left: 10,
		fontWeight: 'bold',
	},
	grid4: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 6,
	},
	colItem: {
		width: '23%',
		marginBottom: 4,
		borderRadius: 4,
		padding: 8,
	},
	cardTitle: { fontSize: 8, color: 'white', opacity: 0.9, marginBottom: 2 },
	cardValue: { fontSize: 11, color: 'white', fontFamily: 'Helvetica-Bold' },
	whiteCard: {
		backgroundColor: 'white',
		paddingVertical: 4,
		paddingHorizontal: 6,
		borderRadius: 3,
		borderWidth: 1,
		borderColor: COLORS.slateLight,
		marginBottom: 4,
		width: '100%',
	},
	whiteCardText: {
		fontSize: 8,
		color: COLORS.slateDark,
		fontFamily: 'Helvetica-Bold',
	},
	tipBox: {
		padding: 10,
		borderRadius: 6,
		marginBottom: 10,
		flexDirection: 'row',
	},
	tipText: {
		fontSize: 9,
		marginLeft: 6,
		lineHeight: 1.3,
		color: 'white',
		flex: 1,
	},
	top100Grid: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		gap: 8,
	},
	top100Column: {
		width: '24%',
		flexDirection: 'column',
	},
	top100Item: {
		width: '100%',
		borderBottomWidth: 1,
		borderBottomColor: 'rgba(226, 232, 240, 0.5)',
		paddingVertical: 2,
	},
	top100Text: { fontSize: 7, color: '#1e293b', fontFamily: 'Helvetica' },
})

const getCount = (str: string) => {
	const match = str.match(/\((\d+)x\)/)

	return match ? parseInt(match[1], 10) : 0
}

function IconScale() {
	return (
		<Svg width='24' height='24' viewBox='0 0 24 24'>
			<Path
				d='M12 3v18M6 12h12M6 12l-3 5h6l-3-5zm12 0l-3 5h6l-3-5z'
				stroke={COLORS.blue}
				strokeWidth={2}
				strokeLinecap='round'
				strokeLinejoin='round'
				fill='none'
			/>
		</Svg>
	)
}

function IconLightbulb({ color = COLORS.yellowText }) {
	return (
		<Svg width='16' height='16' viewBox='0 0 24 24'>
			<Path
				d='M9 21h6v-2H9v2zm3-19C8.48 2 5 5.48 5 10c0 2.76 1.67 5.17 4 6.34V19h6v-2.66c2.33-1.17 4-3.58 4-6.34 0-4.52-3.48-8-8-8z'
				fill={color}
			/>
		</Svg>
	)
}

function IconAlert() {
	return (
		<Svg width='16' height='16' viewBox='0 0 24 24'>
			<Path
				d='M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2zm0-6h2v4h-2z'
				fill={COLORS.brownText}
			/>
		</Svg>
	)
}

function Section({
	children,
	breakPage = false,
	wrap = false,
}: {
	children: React.ReactNode
	breakPage?: boolean
	wrap?: boolean
}) {
	return (
		<View style={styles.sectionBox} break={breakPage} wrap={wrap}>
			{children}
		</View>
	)
}

function Header({ title, icon }: { title: string; icon?: React.ReactNode }) {
	return (
		<>
			<View style={styles.sectionHeader}>
				{icon}
				<Text style={icon ? styles.sectionTitle : styles.sectionTitleNoIcon}>
					{title}
				</Text>
			</View>
			<View style={styles.divider} />
		</>
	)
}

function DistributionCard({
	color,
	title,
	value,
}: {
	color: string
	title: string
	value: string
}) {
	return (
		<View wrap={false} style={[styles.colItem, { backgroundColor: color }]}>
			<Text style={styles.cardTitle}>{title}</Text>
			<Text style={styles.cardValue}>{value}</Text>
		</View>
	)
}

function ThemeCard({
	color,
	title,
	subtitle,
	inverse = false,
}: {
	color: string
	title: string
	subtitle: string
	inverse?: boolean
}) {
	return (
		<View wrap={false} style={[styles.colItem, { backgroundColor: color }]}>
			<Text style={inverse ? styles.cardTitle : styles.cardValue}>{title}</Text>
			<Text style={inverse ? styles.cardValue : styles.cardTitle}>
				{subtitle}
			</Text>
		</View>
	)
}

function WhiteCard({ text }: { text: string }) {
	return (
		<View style={styles.whiteCard} wrap={false}>
			{' '}
			<Text style={styles.whiteCardText}>{text}</Text>
		</View>
	)
}

function ArticleGrid({ list }: { list: string[] }) {
	return (
		<View style={styles.grid4}>
			{list.map((item, i) => (
				<View key={i} style={styles.colItem}>
					<WhiteCard text={item} />
				</View>
			))}
		</View>
	)
}

function InfoBox({
	color,
	icon,
	children,
	columnMode = false,
}: {
	color: string
	icon?: React.ReactNode
	children: React.ReactNode
	columnMode?: boolean
}) {
	return (
		<View
			style={[
				styles.tipBox,
				{ backgroundColor: color },
				columnMode ? { flexDirection: 'column', alignItems: 'stretch' } : {},
			]}
		>
			{icon && icon}
			<Text
				style={[
					styles.tipText,
					{
						color: color === COLORS.yellowGradient ? COLORS.brownText : 'white',
					},
				]}
			>
				{children}
			</Text>
		</View>
	)
}

export function ReportPDF({ data }: { data: PDFData }) {
	if (!data) return null

	const juryNameRaw = data.context.jury || ''
	const isGeneral =
		juryNameRaw.trim() === 'Todas as Bancas' || juryNameRaw.trim() === ''

	const juryPhrase = isGeneral ? `no histórico geral` : `pela ${juryNameRaw}`

	const totalItems = data.lists.top100.length
	const itemsPerCol = Math.ceil(totalItems / 4)

	const columns = Array.from({ length: 4 }, (_, colIndex) => {
		const start = colIndex * itemsPerCol
		const end = start + itemsPerCol

		return data.lists.top100.slice(start, end)
	})

	const sortedArticlesByTheme = data.themes.topByTheme
		.map((item) => {
			const count = getCount(item.value)

			return {
				...item,
				count,
			}
		})
		.sort((a, b) => b.count - a.count)

	const themeList = data.themes.books
	const topTheme = themeList.length > 0 ? themeList[0].title : ''
	const bottomTheme =
		themeList.length > 0 ? themeList[themeList.length - 1].title : ''

	const themesHead = sortedArticlesByTheme.slice(0, 4)
	const themesTail = sortedArticlesByTheme.slice(4)

	return (
		<Document>
			<Page size='A4' style={styles.page} wrap>
				<View style={styles.mainWrapper}>
					<View style={{ alignItems: 'center', marginBottom: 20 }}>
						<PDFLogo />
					</View>

					<Section wrap={false}>
						<Header title='Estatísticas Gerais' icon={<IconScale />} />
						<View style={styles.statsBox}>
							<Text style={styles.badge}>ESTATÍSTICAS</Text>
							<Text style={[styles.textSm, { marginTop: 4 }]}>
								<Text style={styles.bold}>
									Total de artigos únicos cobrados:
								</Text>{' '}
								{data.stats.totalArticles} artigos {data.stats.articleRange}
							</Text>
							<Text style={styles.textSm}>
								<Text style={styles.bold}>Total de questões analisadas:</Text>{' '}
								{data.stats.totalQuestions} questões
							</Text>
							<Text
								style={[
									styles.textSm,
									styles.bold,
									{ marginTop: 8, marginBottom: 2 },
								]}
							>
								10 Artigos Mais Cobrados:
							</Text>
							<View style={{ marginLeft: 4 }}>
								{data.stats.top10.map((item, i) => (
									<Text key={i} style={styles.textSm}>
										{item}
									</Text>
								))}
							</View>
						</View>

						<Text style={styles.subTitle}>Distribuição por Frequência</Text>
						<View
							style={[styles.colorBar, { backgroundColor: COLORS.softRed }]}
						/>
						<View style={styles.grid4}>
							<DistributionCard
								color={COLORS.red}
								title={data.distribution.veryHigh.label}
								value={`${data.distribution.veryHigh.count} artigos`}
							/>
							<DistributionCard
								color={COLORS.orange}
								title={data.distribution.high.label}
								value={`${data.distribution.high.count} artigos`}
							/>
							<DistributionCard
								color={COLORS.green}
								title={data.distribution.medium.label}
								value={`${data.distribution.medium.count} artigos`}
							/>
							<DistributionCard
								color={COLORS.blue}
								title={data.distribution.low.label}
								value={`${data.distribution.low.count} artigos`}
							/>
						</View>
					</Section>

					<Section wrap breakPage>
						<View wrap={false}>
							<Header
								title={`Frequência ${data.distribution.veryHigh.label}`}
								icon={<IconScale />}
							/>
							<InfoBox
								color={COLORS.softRed}
								icon={<IconLightbulb color='yellow' />}
							>
								<Text
									style={{
										fontFamily: 'Helvetica-Bold',
										color: COLORS.yellowText,
									}}
								>
									DICA:{' '}
								</Text>
								{`Estes são os artigos mais cobrados ${juryPhrase} em ${data.context.discipline}. Priorize o estudo detalhado destes dispositivos.`}
							</InfoBox>
						</View>
						<View
							style={[
								styles.tipBox,
								{
									backgroundColor: COLORS.red,
									flexDirection: 'column',
									alignItems: 'stretch',
									marginTop: 10,
								},
							]}
						>
							<Text
								style={[
									styles.bold,
									{ color: 'white', fontSize: 12, marginBottom: 8 },
								]}
							>
								Artigos Prioritários
							</Text>
							<ArticleGrid list={data.lists.priority} />
						</View>
					</Section>

					<Section wrap breakPage>
						<View wrap={false} style={{ marginBottom: 10 }}>
							<Header title={`Frequência ${data.distribution.high.label}`} />
						</View>
						<View
							style={[
								styles.tipBox,
								{
									backgroundColor: COLORS.darkOrange,
									flexDirection: 'column',
									alignItems: 'stretch',
								},
							]}
						>
							<Text
								style={[
									styles.bold,
									{ color: 'white', fontSize: 12, marginBottom: 8 },
								]}
							>
								Artigos Importantes
							</Text>
							<ArticleGrid list={data.lists.important} />
						</View>
					</Section>

					<Section wrap breakPage>
						<View wrap={false} style={{ marginBottom: 10 }}>
							<Header title={`Frequência ${data.distribution.medium.label}`} />
						</View>
						<View
							style={[
								styles.tipBox,
								{
									backgroundColor: COLORS.darkGreen,
									flexDirection: 'column',
									alignItems: 'stretch',
								},
							]}
						>
							<Text
								style={[
									styles.bold,
									{ color: '#0f391e', fontSize: 12, marginBottom: 8 },
								]}
							>
								Artigos com Cobrança Regular
							</Text>
							<ArticleGrid list={data.lists.regular} />
						</View>
					</Section>

					<Section wrap breakPage>
						<View wrap={false}>
							<Header title='Principais Temas Cobrados' icon={<IconScale />} />
							<InfoBox color={COLORS.yellowGradient} icon={<IconAlert />}>
								<Text style={styles.bold}>ATENÇÃO: </Text>
								{isGeneral
									? `O conteúdo de ${data.context.discipline} é explorado de forma abrangente pelas bancas.`
									: `A ${juryNameRaw} explora amplamente todo o conteúdo de ${data.context.discipline}.`}
								{` Desde ${topTheme} até ${bottomTheme}. No entanto, ${topTheme} é mais cobrada.`}
							</InfoBox>
						</View>

						<View style={{ marginTop: 15 }}>
							<View wrap={false}>
								<Text style={[styles.textSm, styles.bold, { marginBottom: 6 }]}>
									Distribuição por Livros de {data.context.discipline}:
								</Text>
								<View style={styles.grid4}>
									{data.themes.books.map((b, i) => (
										<ThemeCard
											key={i}
											color={b.color}
											title={b.subtitle}
											subtitle={b.title}
											inverse
										/>
									))}
								</View>
							</View>
						</View>

						<View style={{ marginTop: 20 }}>
							<View wrap={false}>
								<Text style={[styles.textSm, styles.bold, { marginBottom: 6 }]}>
									Artigos mais cobrados por tema:
								</Text>
								<View style={styles.grid4}>
									{themesHead.map((t, i) => (
										<ThemeCard
											key={i}
											color={t.color}
											title={t.title}
											subtitle={t.value}
										/>
									))}
								</View>
							</View>

							{themesTail.length > 0 && (
								<View style={styles.grid4}>
									{themesTail.map((t, i) => (
										<ThemeCard
											key={`tail-${i}`}
											color={t.color}
											title={t.title}
											subtitle={t.value}
										/>
									))}
								</View>
							)}
						</View>
					</Section>
				</View>
				<View style={styles.mainWrapper}>
					<Section wrap breakPage>
						<Header
							title='Top 100 Artigos Mais Cobrados'
							icon={<IconScale />}
						/>
						<InfoBox
							color={COLORS.softRed}
							icon={<IconLightbulb color='white' />}
						>
							<Text
								style={{
									color: COLORS.yellowText,
									fontFamily: 'Helvetica-Bold',
								}}
							>
								DICA:{' '}
							</Text>
							{`Lista dos 100 artigos mais cobrados de ${data.context.discipline} ${juryPhrase}, ordenados por frequência de cobrança. Inclui artigos com letras.`}
						</InfoBox>
						<View style={styles.top100Grid}>
							{columns.map((columnItems, colIndex) => (
								<View key={colIndex} style={styles.top100Column}>
									{columnItems.map((article, itemIndex) => (
										<View key={itemIndex} style={styles.top100Item}>
											<Text style={styles.top100Text}>{article}</Text>
										</View>
									))}
								</View>
							))}
						</View>
					</Section>
				</View>
			</Page>
		</Document>
	)
}
